// Kit terrain — admin (upload/manage) + conseiller (download)
import { useState, useRef } from "react";
import { C, Btn, Input, Textarea, Modal, FormField, Alert, Spinner, useAsync, documentsApi, fmtSize } from "./shared";

const iconOf = (ext) => {
  const e = (ext || "").toUpperCase();
  if (e === "PDF") return "📄";
  if (["DOCX", "DOC"].includes(e)) return "📝";
  if (["PPTX", "PPT"].includes(e)) return "📊";
  if (["XLSX", "XLS"].includes(e)) return "📈";
  if (["JPG", "JPEG", "PNG"].includes(e)) return "🖼️";
  if (e === "ZIP") return "🗜️";
  if (e === "HTML") return "🌐";
  return "📎";
};

const UploadModal = ({ onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) return setErr("Sélectionnez un fichier");
    if (!nom.trim()) return setErr("Le nom du document est requis");
    setUploading(true); setErr("");
    try {
      await documentsApi.upload(file, nom, description);
      onSaved(); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); }
  };

  return (
    <Modal title="Ajouter un document" onClose={onClose}
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={uploading} onClick={submit}>{uploading ? "Envoi…" : "Ajouter"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FormField label="Fichier" required>
          <div style={{ border: `2px dashed ${file ? C.gold : C.border}`, borderRadius: 10, padding: 20, textAlign: "center", background: file ? C.goldlt : C.gray2, cursor: "pointer" }} onClick={() => inputRef.current?.click()}>
            <input ref={inputRef} type="file" onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { setFile(f); if (!nom) setNom(f.name.replace(/\.[^.]+$/, "")); }
            }} style={{ display: "none" }} />
            {file ? (
              <>
                <div style={{ fontSize: 28 }}>{iconOf(file.name.split(".").pop())}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginTop: 6 }}>{file.name}</div>
                <div style={{ fontSize: 11, color: C.mid, marginTop: 2 }}>{fmtSize(file.size)}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 28, color: C.muted }}>📁</div>
                <div style={{ fontSize: 13, color: C.mid, marginTop: 6 }}>Cliquez pour sélectionner un fichier</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>PDF, DOCX, PPTX, XLSX, JPG, ZIP… (max 15 Mo)</div>
              </>
            )}
          </div>
        </FormField>
        <FormField label="Nom du document" required>
          <Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex: Guide d'entretien — Consultation initiale" />
        </FormField>
        <FormField label="Description (optionnelle)">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brève description du document…" />
        </FormField>
      </div>
    </Modal>
  );
};

const EditModal = ({ doc, onClose, onSaved }) => {
  const [nom, setNom] = useState(doc.nom);
  const [description, setDescription] = useState(doc.description || "");
  const [actif, setActif] = useState(doc.actif);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!nom.trim()) return setErr("Nom requis");
    setSaving(true); setErr("");
    try {
      await documentsApi.update(doc.id, { nom, description, actif });
      onSaved(); onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Modifier · ${doc.filename}`} onClose={onClose}
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving} onClick={submit}>{saving ? "…" : "Enregistrer"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FormField label="Nom" required><Input value={nom} onChange={(e) => setNom(e.target.value)} /></FormField>
        <FormField label="Description"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} /></FormField>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.gold }} />
          <span style={{ fontSize: 13 }}>Document actif (visible pour les consultants)</span>
        </label>
        <div style={{ background: C.gray2, borderRadius: 8, padding: 12, fontSize: 12, color: C.mid }}>
          <div><strong>Fichier :</strong> {doc.filename}</div>
          <div><strong>Taille :</strong> {fmtSize(doc.size)}</div>
          <div><strong>Ajouté le :</strong> {new Date(doc.uploadedAt).toLocaleString("fr-FR")}</div>
          <div><strong>Par :</strong> {doc.uploadedBy || "—"}</div>
        </div>
      </div>
    </Modal>
  );
};

const DocCard = ({ d, isAdmin, onEdit, onDelete, onDownload }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 14, display: "flex", alignItems: "center", gap: 12, opacity: d.actif ? 1 : .55 }}>
    <div style={{ width: 46, height: 46, borderRadius: 10, background: (d.color || C.mid) + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
      {iconOf(d.ext)}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.nom}</div>
      {d.description && <div style={{ fontSize: 11, color: C.mid, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.description}</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: d.color || C.mid, background: (d.color || C.mid) + "18", padding: "1px 6px", borderRadius: 4 }}>{d.ext}</span>
        <span style={{ fontSize: 10, color: C.muted }}>{fmtSize(d.size)}</span>
        {isAdmin && !d.actif && <span style={{ fontSize: 10, color: C.amber, fontWeight: 600 }}>· Inactif</span>}
      </div>
    </div>
    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
      <Btn small onClick={() => onDownload(d)}>↓</Btn>
      {isAdmin && <>
        <Btn small onClick={() => onEdit(d)}>✎</Btn>
        <Btn small variant="red" onClick={() => onDelete(d)}>×</Btn>
      </>}
    </div>
  </div>
);

export const KitTerrainAdmin = () => {
  const { data: docs, loading, reload } = useAsync(documentsApi.list, []);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);

  if (loading) return <Spinner />;

  const download = (d) => documentsApi.download(d.id, d.filename);
  const remove = async (d) => {
    if (!window.confirm(`Supprimer "${d.nom}" ?`)) return;
    await documentsApi.remove(d.id); reload();
  };

  return (
    <>
      <Alert type="info">
        <span>📁</span>
        <span>Uploadez les documents du <strong>Kit terrain</strong>. Vos consultants pourront les télécharger depuis leur espace. Max 15 Mo par fichier.</span>
      </Alert>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 14, color: C.mid }}>{docs?.length || 0} document{(docs?.length || 0) > 1 ? "s" : ""}</div>
        <Btn variant="gold" onClick={() => setShowUpload(true)}>+ Ajouter un document</Btn>
      </div>
      {(!docs || docs.length === 0) ? (
        <div style={{ textAlign: "center", padding: 60, color: C.muted, background: "#fff", borderRadius: 12, border: `.5px dashed ${C.border}` }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: .4 }}>📂</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Aucun document</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>Commencez par ajouter vos premiers documents Kit terrain</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
          {docs.map(d => <DocCard key={d.id} d={d} isAdmin onEdit={setEditing} onDelete={remove} onDownload={download} />)}
        </div>
      )}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSaved={reload} />}
      {editing && <EditModal doc={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </>
  );
};

// Vue conseiller — téléchargement uniquement
export const KitTerrain = () => {
  const { data: docs, loading } = useAsync(documentsApi.list, []);
  if (loading) return <Spinner />;
  const download = (d) => documentsApi.download(d.id, d.filename);

  return (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".09em", margin: "0 0 12px" }}>Kit terrain complet</div>
      {(!docs || docs.length === 0) ? (
        <div style={{ textAlign: "center", padding: 60, color: C.muted, background: "#fff", borderRadius: 12, border: `.5px dashed ${C.border}` }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: .4 }}>📂</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Aucun document disponible pour le moment</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>Les documents seront bientôt mis à disposition par l'équipe AUPRÉA.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
          {docs.map(d => <DocCard key={d.id} d={d} isAdmin={false} onDownload={download} />)}
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <Alert type="warn"><span>⚠️</span><span>Documents <strong>confidentiels AUPRÉA</strong>. Ne pas partager en dehors du cadre professionnel.</span></Alert>
      </div>
    </>
  );
};
